import { getCurrencyDisplay, type MarketCountry } from "@/config/market";

export interface ProductCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  translations?: Record<string, { name?: string; description?: string }>;
}

// Interface for blood value items
export interface BloodValueItem {
  value: boolean;
  sort_order?: number;
  name?: string | Record<string, string>; // Old format: embedded translations map
  nameKey?: string; // New format: translation key to be resolved from locale files
}

// Interface for scan feature items
export interface ScanFeatureItem {
  value: boolean;
  sort_order?: number;
  name?: string | Record<string, string>; // Old format: embedded translations map
  nameKey?: string; // New format: translation key to be resolved from locale files
}

// Interface for product translations
export interface ProductTranslations {
  scan_features?: Record<string, string>;
  blood_values?: Record<string, string>;
  radiation_details?: string;
  name?: string;
  description?: string;
  short_description?: string;
  [key: string]: unknown;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  short_description?: string | null;
  images: string[];
  default_price: {
    id: string;
    unit_amount: number;
    currency: string;
  };
  /** Member-specific price from Stripe (nickname "membership"). Present only when such a price exists. */
  member_price?: {
    id: string;
    unit_amount: number;
    currency: string;
  };
  // New fields from our Supabase products table
  stripe_product_id?: string;
  /** Mode-agnostic Stripe product handle (matches metadata.stripe_metadata_id
   *  on the Stripe product). Lets the checkout edge function resolve the
   *  account-correct product id in either prod or sandbox. */
  stripe_metadata_id?: string | null;
  /** Stable per-product slug ('core', 'advanced', 'pro', 'pro-men'). The one
   *  identity column with a UNIQUE constraint: `stripe_metadata_id` is shared
   *  across rows that map to a single Stripe product. */
  human_readable_id?: string | null;
  status?: "active" | "development" | "archived" | "membership";
  mandatory?: boolean;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
  category_id?: string;
  category?: ProductCategory;
  // Package specific fields
  product_type?: "standard" | "package" | "addon";
  package_types?: string[]; // Array of package types this product supports: ['mri'], ['blood-only'], or ['mri', 'blood-only']
  components?: string[];
  duration_minutes?: number;
  radiation_details?: string | null;
  contrast_dye?: boolean;
  scan_features?: Record<string, boolean | ScanFeatureItem>;
  blood_values?: Record<string, boolean | BloodValueItem>;
  is_popular?: boolean;
  display_order?: number;
  // Translations
  translations?: ProductTranslations;
  warning?: string;
  // Consultation requirement
  requires_consultation_beforehand?: number;
  // Blood draw
  includes_blood_draw?: boolean;
  // Add-ons that should not be offered when this package is selected
  excluded_addon_ids?: string[];
  // Optional qualifier pill shown next to the add-on name (a translation key
  // resolved via t(), e.g. "addOnsStep.qualifier.onceForLife").
  qualifier_tag_key?: string | null;
  // USP data keyed by section name (e.g. booking_flow_first_page), then by
  // language or "data". The inner shape is intentionally loose because it
  // comes from a CMS with evolving structure.
  usps?: Record<string, Record<string, unknown>>;
}

// Helper function to normalize language codes for translation lookup
export const normalizeLanguage = (language: string): string => {
  // Handle URL parameter short codes and convert to full language codes
  if (language === "de") return "de-DE";
  if (language === "en") return "en-US";
  if (language === "fr") return "fr-FR";
  if (language === "nl") return "nl-NL";

  // Handle full codes
  if (
    language === "en-US" ||
    language === "de-DE" ||
    language === "fr-FR" ||
    language === "nl-NL"
  ) {
    return language;
  }

  // Default to German
  return "de-DE";
};

/**
 * Format a whole-currency amount for a market. All presentation — symbol,
 * before/after/none placement, thousands grouping and the Dutch ",-" suffix —
 * comes from the market's `CurrencyDisplay` (config/market), so this is purely
 * a market concern, never a language one.
 *
 * `excludeCurrency` drops the standalone symbol (the caller renders it as its
 * own styled span) but keeps the Dutch ",-" notation, which is part of the
 * amount rather than a symbol.
 */
export const formatPrice = (
  amount: number,
  market: MarketCountry,
  excludeCurrency: boolean = false,
) => {
  const {
    symbol,
    position,
    groupingLocale,
    swissApostrophe,
    wholeAmountSuffix,
  } = getCurrencyDisplay(market);
  // Whole francs render as before. A non-whole amount renders its centimes
  // instead of being floored away: a discounted total really can be CHF 2.41,
  // and flooring it to "CHF 2" showed the customer a number we do not charge.
  // The whole-amount suffix (NL's ",-") is a whole-amount convention, so it is
  // dropped when centimes are shown.
  const isWholeAmount = amount % 100 === 0;
  const price = isWholeAmount ? Math.floor(amount / 100) : amount / 100;

  let grouping = price.toLocaleString(groupingLocale, {
    useGrouping: true,
    minimumFractionDigits: isWholeAmount ? 0 : 2,
    maximumFractionDigits: isWholeAmount ? 0 : 2,
  });
  if (swissApostrophe) grouping = grouping.replace(/\s/g, "'");

  const amountText = `${grouping}${isWholeAmount ? wholeAmountSuffix : ""}`;

  if (excludeCurrency || position === "none") return amountText;
  return position === "before"
    ? `${symbol} ${amountText}`
    : `${amountText} ${symbol}`;
};
